/**
 * <gallery-progress> — mobile PDP scroll-progress bar.
 *
 * Replaces the thumbnail row on mobile with a single growing bar (Native-Pet style).
 * The fill is scaled from 10% -> 100% in lock-step with the horizontal scroll position
 * of the associated <scroll-carousel> (referenced via aria-controls). No dependencies.
 *
 * Rendered by snippets/product-gallery.liquid only when the main-product section's
 * `mobile_controls` setting is "progress_bar". Styling lives in theme.css
 * (.product-gallery__progress / .product-gallery__progress-fill).
 */
(function () {
  if (typeof customElements === 'undefined' || customElements.get('gallery-progress')) {
    return;
  }

  class GalleryProgress extends HTMLElement {
    connectedCallback() {
      this.scroller = document.getElementById(this.getAttribute('aria-controls'));
      this.fill = this.querySelector('.product-gallery__progress-fill');
      if (!this.scroller || !this.fill) {
        return;
      }

      this._ticking = false;
      this._onScroll = this._requestUpdate.bind(this);

      this.scroller.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      window.addEventListener('load', this._onScroll, { passive: true });
      // A variant change filters media in/out of the carousel — recompute then.
      this.scroller.addEventListener('carousel:filter', this._onScroll);
      this.scroller.addEventListener('carousel:change', this._onScroll);

      this._update();
    }

    disconnectedCallback() {
      if (this.scroller) {
        this.scroller.removeEventListener('scroll', this._onScroll);
        this.scroller.removeEventListener('carousel:filter', this._onScroll);
        this.scroller.removeEventListener('carousel:change', this._onScroll);
      }
      window.removeEventListener('resize', this._onScroll);
      window.removeEventListener('load', this._onScroll);
    }

    _requestUpdate() {
      if (this._ticking) {
        return;
      }
      this._ticking = true;
      requestAnimationFrame(() => {
        this._ticking = false;
        this._update();
      });
    }

    _update() {
      const el = this.scroller;
      if (!el || !this.fill) {
        return;
      }

      const max = el.scrollWidth - el.clientWidth;
      if (max <= 1) {
        // Only one image is visible (or nothing overflows) — nothing to indicate.
        this.setAttribute('hidden', '');
        return;
      }
      this.removeAttribute('hidden');

      const fraction = Math.min(1, Math.max(0, el.scrollLeft / max));
      // 10% floor so the bar is always visible at the start, growing to 100% (matches Native Pet).
      this.fill.style.transform = 'scaleX(' + (0.1 + fraction * 0.9) + ')';
    }
  }

  customElements.define('gallery-progress', GalleryProgress);
})();
